import { computed, inject, Injectable, signal } from '@angular/core';
import { TripService } from './trip.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Accommodation, AccommodationType } from '../models/accommodation';
import { FactorService } from 'src/app/factors/services/factor.service';
import { EmissionFactorCategory } from 'src/app/factors/types/factor';

@Injectable({
  providedIn: 'root',
})
export class AccommodationFormService {
  tripService = inject(TripService);
  fb = inject(FormBuilder);
  factorService = inject(FactorService);
  mode = signal<'create' | 'edit'>('create');
  index = signal<number | null>(null);
  accommodationForm: FormGroup = this.createAccommodationGroup();

  customFactors = this.factorService.customFactors;
  accommodationFactor = computed(() => {
    return this.customFactors().filter(
      (factor) => factor.category === EmissionFactorCategory.Accommodation,
    );
  });

  createAccommodationGroup(): FormGroup {
    return this.fb.group({
      title: [''],
      countryCode: [this.countryCode, Validators.required],
      type: ['', Validators.required],
      peoplePerRoom: [1, [Validators.required, Validators.min(1)]],
      isEcoFriendly: [false, Validators.required],
      nights: [this.RemainingNights, [Validators.required, Validators.min(1)]],
      scheduledDays: [[]],
    });
  }
  resetFormGroup() {
    this.accommodationForm = this.createAccommodationGroup();
    this.mode.set('create');
    this.index.set(null);
  }

  submitAccommodation() {
    const accommodation: Accommodation = this.accommodationForm.value;

    let result;
    if (this.mode() === 'edit' && this.index() !== null) {
      result = this.tripService.updateAccommodation(
        accommodation,
        this.index()!,
      );
    } else {
      result = this.tripService.addAccommodation(accommodation);
    }

    // Validation will be triggered by trip service after save completes

    return result;
  }

  editAccommodation(index: number) {
    this.mode.set('edit');
    this.index.set(index);
    const accommodation = this.tripService.trip()?.accommodations[index];
    if (!accommodation) {
      console.error('could not load accommodation');
      return;
    }
    if (accommodation.type === AccommodationType.CustomFactor) {
      this.createCustomFactorControl();
    }
    // Reset the form with values instead of patchValue to avoid "phantom values"
    this.accommodationForm.reset(accommodation);
  }

  get RemainingNights() {
    const trip = this.tripService.trip();
    if (!trip) return 0;
    const totalNights = trip.days - 1;
    let bookedNights = 0;
    trip.accommodations.forEach((accommodation) => {
      bookedNights += accommodation.nights;
    });
    const remainingNights = totalNights - bookedNights;
    return remainingNights > 0 ? remainingNights : 0;
  }

  get countryCode() {
    const trip = this.tripService.trip();
    if (!trip) return '';
    return trip.countryCode;
  }

  get hasCustomFactor() {
    return this.accommodationFactor().length > 0;
  }

  get displayCustomEmissionFactors() {
    const type = this.accommodationForm.get('type')?.value;
    return type === AccommodationType.CustomFactor;
  }

  createCustomFactorControl() {
    if (!this.accommodationForm.contains('customEmissionFactorId')) {
      this.accommodationForm.addControl(
        'customEmissionFactorId',
        this.fb.control('', [Validators.required]),
      );
    }
  }
  destroyCustomFactorControl() {
    if (this.accommodationForm.contains('customEmissionFactorId')) {
      this.accommodationForm.removeControl('customEmissionFactorId');
    }
  }
}
